import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Bot, ArrowLeft, MoreVertical, Trash2, Archive, Sparkles, BookOpen, Target } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import {
  getAgents,
  createAgent,
  deleteAgent,
  updateAgent,
  AIAgent,
  AIAgentKind,
  LEAD_AGENT_GOAL_OPTIONS,
  type LeadAgentGoalId,
} from '../lib/aiAgents';
import toast from 'react-hot-toast';

export default function AIAgents() {
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const [agents, setAgents] = useState<AIAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createPurpose, setCreatePurpose] = useState('');
  const [creating, setCreating] = useState(false);
  const [createAgentKind, setCreateAgentKind] = useState<AIAgentKind>('generic');
  const [createLeadGoal, setCreateLeadGoal] = useState<LeadAgentGoalId>('gather_info_sales_call');
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  useEffect(() => {
    if (selectedBusiness?.id) {
      loadAgents();
    }
  }, [selectedBusiness?.id]);

  useEffect(() => {
    const handleClickOutside = () => setMenuOpenId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const loadAgents = async () => {
    if (!selectedBusiness?.id) return;
    try {
      const data = await getAgents(selectedBusiness.id);
      setAgents(data);
    } catch (err) {
      console.error('Failed to load agents:', err);
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedBusiness?.id || !createName.trim()) return;
    setCreating(true);
    try {
      const agent = await createAgent({
        businessId: selectedBusiness.id,
        name: createName.trim(),
        purpose: createPurpose.trim() || undefined,
        agentKind: createAgentKind,
        leadGoal: createAgentKind === 'lead' ? createLeadGoal : null,
      });
      setShowCreateModal(false);
      setCreateName('');
      setCreatePurpose('');
      setCreateAgentKind('generic');
      setCreateLeadGoal('gather_info_sales_call');
      navigate(`/ai-agents/${agent.id}`);
    } catch (err) {
      console.error('Failed to create agent:', err);
      toast.error('Failed to create agent');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (agentId: string) => {
    if (!confirm('Are you sure you want to delete this agent? This cannot be undone.')) return;
    try {
      await deleteAgent(agentId);
      setAgents(prev => prev.filter(a => a.id !== agentId));
      toast.success('Agent deleted');
    } catch (err) {
      toast.error('Failed to delete agent');
    }
  };

  const handleArchive = async (agentId: string) => {
    try {
      await updateAgent(agentId, { status: 'archived' });
      setAgents(prev => prev.filter(a => a.id !== agentId));
      toast.success('Agent archived');
    } catch (err) {
      toast.error('Failed to archive agent');
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'draft': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div
      className="h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col"
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}
    >
      {/* Header */}
      <div className="bg-white border-b border-gray-200/60 px-6 py-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/automations')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>AI Agents</h1>
              <span className="px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded-full bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-sm">
                Beta
              </span>
            </div>
            <p className="text-sm text-gray-500">Train custom AI agents for your business</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors shadow-sm"
        >
          <Plus className="h-4 w-4" />
          Create Agent
        </button>
      </div>

      {/* Beta Banner */}
      <div className="mx-6 mt-4 px-4 py-3 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200/60 rounded-xl flex items-start gap-3">
        <Sparkles className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-purple-900">This feature is currently in Beta</p>
          <p className="text-xs text-purple-700/80 mt-0.5">AI Agents is an early-access feature. Functionality may change and improvements are being made regularly.</p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : agents.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="w-20 h-20 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
              <Bot className="h-10 w-10 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No agents yet</h3>
            <p className="text-gray-500 max-w-md mb-6">
              Create your first AI agent and train it by having a conversation about your business.
              Just talk naturally -- the AI will learn your pricing, rules, and processes.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors"
            >
              <Sparkles className="h-4 w-4" />
              Create Your First Agent
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl">
            {agents.map(agent => (
              <div
                key={agent.id}
                className="bg-white rounded-2xl border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group relative"
                onClick={() => navigate(`/ai-agents/${agent.id}`)}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{agent.icon || '🤖'}</span>
                      <div>
                        <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">{agent.name}</h3>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full capitalize ${statusColor(agent.status)}`}>
                            {agent.status}
                          </span>
                          {agent.agent_kind === 'lead' && (
                            <span className="inline-block text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-violet-100 text-violet-800">
                              Lead
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(menuOpenId === agent.id ? null : agent.id);
                      }}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <MoreVertical className="h-4 w-4 text-gray-400" />
                    </button>
                    {menuOpenId === agent.id && (
                      <div className="absolute right-4 top-14 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-10 min-w-[140px]">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleArchive(agent.id); setMenuOpenId(null); }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Archive className="h-3.5 w-3.5" /> Archive
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(agent.id); setMenuOpenId(null); }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                  {agent.purpose && (
                    <p className="text-sm text-gray-500 line-clamp-2 mb-3">{agent.purpose}</p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <BookOpen className="h-3 w-3" />
                      {agent.rule_count || 0} rules
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Agent Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Create AI Agent</h2>
              <p className="text-sm text-gray-500 mt-1">Choose a type, then name your agent</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Agent type</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setCreateAgentKind('generic')}
                    className={`text-left rounded-xl border-2 p-4 transition-all ${
                      createAgentKind === 'generic'
                        ? 'border-blue-500 bg-blue-50/60 shadow-sm'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Bot className="h-5 w-5 text-blue-600" />
                      <span className="font-semibold text-gray-900">Generic</span>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Train on pricing and policies — same experience as today. No CRM tools.
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCreateAgentKind('lead');
                      setCreateLeadGoal('gather_info_sales_call');
                    }}
                    className={`text-left rounded-xl border-2 p-4 transition-all ${
                      createAgentKind === 'lead'
                        ? 'border-violet-500 bg-violet-50/60 shadow-sm'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Target className="h-5 w-5 text-violet-600" />
                      <span className="font-semibold text-gray-900">Lead agent</span>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Same training, plus optional SMS and CRM tools when assigned to an opportunity.
                    </p>
                  </button>
                </div>
              </div>
              {createAgentKind === 'lead' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Lead agent goal</label>
                  <p className="text-xs text-gray-500 mb-2">
                    Preset guides live SMS behavior in the CRM; training still defines which questions to ask.
                  </p>
                  <select
                    value={createLeadGoal}
                    onChange={(e) => setCreateLeadGoal(e.target.value as LeadAgentGoalId)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none bg-white"
                  >
                    {LEAD_AGENT_GOAL_OPTIONS.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-500 mt-1.5">
                    {LEAD_AGENT_GOAL_OPTIONS.find((g) => g.id === createLeadGoal)?.description}
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Agent Name</label>
                <input
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="e.g., Junk Removal Estimator"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">What does this agent do?</label>
                <textarea
                  value={createPurpose}
                  onChange={(e) => setCreatePurpose(e.target.value)}
                  placeholder="e.g., Estimates the cost of junk removal jobs based on photos and descriptions"
                  rows={3}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateName('');
                  setCreatePurpose('');
                  setCreateAgentKind('generic');
                  setCreateLeadGoal('gather_info_sales_call');
                }}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!createName.trim() || creating}
                className="px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {creating ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                Create & Train
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
